import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { q } from "../api.ts";
import { currentProduct } from "../product-filter.tsx";
import { GATE_LABELS, KIT_LABELS as K, LANGUAGE_LABELS, AGENT_PAGE_LABELS as G, NEW_PRODUCT_LABELS, SETTINGS_LABELS as L, SETTINGS_NAV_LABELS as N } from "../labels.ts";
import { GatePanel } from "../gate-report.tsx";
import { Grants } from "../grants.tsx";
import { LanguagePicker } from "../language-picker.tsx";
import { LocalLauncher } from "../local-launcher.tsx";
import { CloudAgentSteps, cloudPrompt } from "../cloud-agent.tsx";
import { PersonalTokens } from "../personal-tokens.tsx";
import { type Contrast, contrast, setContrast, setTheme, type Theme, theme } from "../prefs.ts";
import { ProductSettings } from "../product-settings.tsx";
import { RunSettings } from "../run-settings.tsx";
import { Button, CopyButton, ErrorText, Field, Page, Section } from "../ui.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { WakeupSettings } from "../wakeup.tsx";
import { DispatchSettings } from "../dispatch.tsx";

// Settings (docs/design-system.md §0.2, plan M7 step 7d): one page per
// section, reached from settings' own navigation in place of the sidebar.
// The workspace's sections first, then each product's: basics, agent and
// runs, pre-authorizations. Governance here takes a person's session; the
// server enforces it.

function SettingsPage({ trail, title, children }: { trail: string[]; title: string; children: ReactNode }) {
  return <Page crumbs={[{ label: L.title }, ...trail.map((label) => ({ label }))]} title={title}><div className="z-settings">{children}</div></Page>;
}

function useMe() {
  return useQuery(q.me);
}

// ------------------------------------------------------------ workspace

export function TokensSettings() {
  const me = useMe();
  return <SettingsPage trail={[N.tokens]} title={N.tokens}>
    {me.isLoading ? <Skeleton shape="list" rows={2} /> : me.data ? <PersonalTokens key={`${me.data.workspace.id}:${me.data.user.id}`} workspaceId={me.data.workspace.id} userId={me.data.user.id} /> : <ErrorText error={me.error} />}
  </SettingsPage>;
}

/** The person's own: the language is kept on the account; theme and contrast on this device. */
export function PreferencesSettings() {
  const [value, setValue] = useState<Contrast>(contrast);
  const [shown, setShown] = useState<Theme>(theme);
  return <SettingsPage trail={[N.preferences]} title={N.preferences}>
    <Section title={LANGUAGE_LABELS.title}><LanguagePicker account /></Section>
    <Section title={L.appearance} note={L.appearanceNote}>
      <div className="z-pref-grid">
        <span aria-hidden>{L.theme}</span>
        <div className="z-segmented" role="group" aria-label={L.theme}>
          {(Object.keys(L.themeOptions) as Theme[]).map((option) => <button key={option} type="button" className={shown === option ? "is-on" : undefined} aria-pressed={shown === option} onClick={() => { setTheme(option); setShown(option); }}>{L.themeOptions[option]}</button>)}
        </div>
        <span aria-hidden>{L.contrast}</span>
        <div className="z-segmented" role="group" aria-label={L.contrast}>
          {(Object.keys(L.contrastOptions) as Contrast[]).map((option) => <button key={option} type="button" className={value === option ? "is-on" : undefined} aria-pressed={value === option} onClick={() => { setContrast(option); setValue(option); }}>{L.contrastOptions[option]}</button>)}
        </div>
      </div>
    </Section>
  </SettingsPage>;
}

/** Operators only (plan M5 step 5e): the server refuses anyone else. */
export function GateSettings() {
  return <SettingsPage trail={[GATE_LABELS.tab]} title={GATE_LABELS.tab}><GatePanel /></SettingsPage>;
}

// ------------------------------------------------------------ a product

function useSettingsProduct() {
  const { productId } = useParams({ strict: false }) as { productId?: string };
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  return { products, product };
}

function ProductSettingsPage({ section, children }: { section: string; children: (product: C.Product) => ReactNode }) {
  const { products, product } = useSettingsProduct();
  if (products.isLoading) return <SettingsPage trail={[section]} title={section}><Skeleton shape="form" /></SettingsPage>;
  if (!product) return <SettingsPage trail={[section]} title={section}><ErrorText error={products.error} /></SettingsPage>;
  return <SettingsPage trail={[product.name, section]} title={section}>{children(product)}</SettingsPage>;
}

export function ProductBasicsSettings() {
  const { productId } = useParams({ strict: false }) as { productId?: string };
  const detail = useQuery({ ...q.product(productId ?? ""), enabled: Boolean(productId) });
  return <ProductSettingsPage section={N.basics}>{(product) => detail.data ? <ProductSettings key={product.id} product={detail.data} /> : <><ErrorText error={detail.error} />{detail.isLoading && <Skeleton shape="form" />}</>}</ProductSettingsPage>;
}

/** Every member's live agent connections to the product, by the rule the start checklist uses: whose, how connected and when last used. */
function Connection({ product }: { product: C.Product }) {
  const connections = useQuery(q.agentConnections(product.id));
  return <Section title={G.connection}>
    {connections.isLoading ? <Skeleton shape="list" rows={1} /> : connections.data && (connections.data.length ? <div className="z-boxed">
      {connections.data.map((connection) => <div key={connection.id} className="z-lrow">
        <span className="z-lrow-text">
          <span className="z-lrow-title">{connection.mine ? K.you : connection.owner.name || connection.owner.email}</span>
          <span className="z-lrow-sub">{G.source[connection.source](connection.name)}</span>
        </span>
        <span className="z-lrow-meta">{connection.last_used_at ? G.lastUsed(connection.last_used_at) : G.notUsed}</span>
      </div>)}
    </div> : <p>{G.none}</p>)}
    <ErrorText error={connections.error} />
  </Section>;
}

/** Grok Bot and OpenAI dots run in their vendor's cloud and connect without the plugin; folded, since it is experimental. */
function CloudAgent({ product }: { product: C.Product }) {
  return <Section id="cloud-agent" title={NEW_PRODUCT_LABELS.cloudTitle}>
    <details className="z-disclosure">
      <summary>{NEW_PRODUCT_LABELS.cloudSummary}</summary>
      <div className="z-flow">
        <CloudAgentSteps footer={<div className="z-inline"><CopyButton text={cloudPrompt(product)} label={NEW_PRODUCT_LABELS.copyForAgent} /></div>} />
      </div>
    </details>
  </Section>;
}

/** How the person's agent connects to the product, the one-click launcher and when zooi asks it to run. */
export function ProductAgentSettings() {
  const me = useMe();
  const hash = useRouterState({ select: (state) => state.location.hash });
  const scrolled = useRef(false);
  useEffect(() => {
    if (!hash || scrolled.current || !me.data) return;
    scrolled.current = true;
    requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ block: "start" }));
  }, [hash, me.data]);
  return <ProductSettingsPage section={N.agent}>{(product) => me.data ? <>
    <LocalLauncher key={`launcher:${product.id}`} scope={`${me.data.workspace.id}:${me.data.user.id}`} product={product} />
    <CloudAgent product={product} />
    <Connection product={product} />
    <RunSettings key={product.id} product={product} />
    <WakeupSettings key={`wakeup:${product.id}`} product={product} />
    <DispatchSettings key={`dispatch:${product.id}`} product={product} />
  </> : <ErrorText error={me.error} />}</ProductSettingsPage>;
}

export function ProductGrantsSettings() {
  return <ProductSettingsPage section={N.grants}>{(product) => <Grants key={product.id} product={product} />}</ProductSettingsPage>;
}

// ------------------------------------------------------------ old addresses

/** `/settings?tab=…` and `/agent?product=` from before settings had their own pages. */
export function SettingsIndex() {
  const navigate = useNavigate();
  const search = useRouterState({ select: (state) => state.location.search as { tab?: string; product?: string } });
  const hash = useRouterState({ select: (state) => state.location.hash });
  const products = useQuery(q.products);
  const done = useRef(false);
  useEffect(() => {
    if (done.current || products.isLoading) return;
    done.current = true;
    const product = currentProduct(products.data ?? [], search.product);
    const forProduct = (to: "/settings/products/$productId" | "/settings/products/$productId/agent") => product
      ? navigate({ to, params: { productId: product.id }, hash: hash || undefined, replace: true })
      : navigate({ to: "/new", replace: true });
    switch (search.tab) {
      case "product": void forProduct("/settings/products/$productId"); break;
      case "runs": case "agent": void forProduct("/settings/products/$productId/agent"); break;
      case "tokens": void navigate({ to: "/settings/tokens", replace: true }); break;
      case "preferences": case "appearance": void navigate({ to: "/settings/preferences", replace: true }); break;
      case "gate": void navigate({ to: "/settings/gate", replace: true }); break;
      case "rules": void navigate({ to: "/playbooks", search: { view: "rules" }, replace: true }); break;
      case "data": void (product ? navigate({ to: "/p/$productId/data", params: { productId: product.id }, search: { view: "sources" }, replace: true }) : navigate({ to: "/new", replace: true })); break;
      default: void navigate({ to: "/settings/members", replace: true });
    }
  }, [hash, navigate, products.data, products.isLoading, search.product, search.tab]);
  return <Page crumbs={[]}><Skeleton shape="form" /></Page>;
}

/** `/agent?product=`: the agent page became Settings › product › Agent and runs. */
export function AgentRedirect() {
  const navigate = useNavigate();
  const search = useRouterState({ select: (state) => state.location.search as { product?: string } });
  const hash = useRouterState({ select: (state) => state.location.hash });
  const products = useQuery(q.products);
  const done = useRef(false);
  useEffect(() => {
    if (done.current || products.isLoading) return;
    done.current = true;
    const product = currentProduct(products.data ?? [], search.product);
    void (product ? navigate({ to: "/settings/products/$productId/agent", params: { productId: product.id }, hash: hash || undefined, replace: true }) : navigate({ to: "/new", replace: true }));
  }, [hash, navigate, products.data, products.isLoading, search.product]);
  return <Page crumbs={[]}><Skeleton shape="form" /></Page>;
}
